import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faShoppingCart, faHeart, faBars, faTimes, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useFilters } from '../../contexts/FilterContext';
import { categoryService, productService } from '../../services/api';
import styles from './Header.module.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeSection, setActiveSection] = useState('');
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const { user, isAuthenticated, logout } = useAuth();
  const { totalItems } = useCart();
  const { updateCategoryFilter, categoryFilter, clearFilters } = useFilters();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    closeMenu();
  };

  // Charger les catégories au montage
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const response = await categoryService.getCategories();
        if (response.success) {
          const categoriesData = Array.isArray(response.data)
            ? response.data
            : (response.data?.categories || response.data?.data || []);
          // Limiter aux 5 premières catégories actives pour le menu
          setCategories(categoriesData.filter(cat => cat.is_active !== false).slice(0, 5));
        }
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Recherche en temps réel avec debounce
  const performSearch = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setIsSearching(true);
      const response = await productService.searchProducts({
        q: query.trim(),
        per_page: 5 // Limiter à 5 résultats pour les suggestions
      });

      if (response.success && response.data) {
        setSearchSuggestions(response.data.data || []);
        setShowSuggestions(true);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce de la recherche
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300); // Attendre 300ms après la dernière frappe
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Fermer les suggestions quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Détecter la section active selon la route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setActiveSection('home');
    } else if (path === '/products' || path.startsWith('/product/')) {
      setActiveSection('products');
    } else if (path === '/cart') {
      setActiveSection('cart');
    } else if (path === '/checkout') {
      setActiveSection('checkout');
    } else {
      setActiveSection('');
    }
  }, [location]);

  // Gérer le clic sur une catégorie
  const handleCategoryClick = (categoryId, e) => {
    e.preventDefault();
    closeMenu();

    // Si la catégorie est déjà sélectionnée, désélectionner (toggle)
    if (categoryFilter === categoryId.toString()) {
      clearFilters();
    } else {
      // Sinon, appliquer le filtre de catégorie
      updateCategoryFilter(categoryId.toString());
    }

    // Naviguer vers la page d'accueil si on n'y est pas déjà
    if (location.pathname !== '/') {
      navigate('/');
    }

    // Scroll vers la section produits
    setTimeout(() => {
      const productsSection = document.getElementById('products-section');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Gérer le clic sur Accueil
  const handleHomeClick = (e) => {
    e.preventDefault();
    closeMenu();
    // Effacer les filtres quand on clique sur Accueil
    clearFilters();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  // Gérer le clic sur Produits
  const handleProductsClick = (e) => {
    e.preventDefault();
    closeMenu();
    if (location.pathname === '/') {
      const productsSection = document.getElementById('products-section');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const productsSection = document.getElementById('products-section');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.headerTop}>
          <Link to="/" className={styles.logo}>Di<span>kass</span></Link>

          <div className={styles.searchBar}>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (searchQuery.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                setSearchQuery('');
                setShowSuggestions(false);
              }
            }}>
              <div className={styles.searchInputWrapper}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Rechercher des produits..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value.trim().length >= 2) {
                      setShowSuggestions(true);
                    } else {
                      setShowSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (searchSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                />
                <button type="submit" className={styles.searchButton}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </button>
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div ref={suggestionsRef} className={styles.suggestions}>
                    {isSearching && (
                      <div className={styles.suggestionItem}>
                        <span>Recherche en cours...</span>
                      </div>
                    )}
                    {!isSearching && searchSuggestions.map((product) => (
                      <div
                        key={product.id}
                        className={styles.suggestionItem}
                        onClick={() => {
                          navigate(`/product/${product.id}`);
                          setSearchQuery('');
                          setShowSuggestions(false);
                        }}
                      >
                        <div className={styles.suggestionImage}>
                          {product.images && product.images.length > 0 ? (
                            <img src={product.images[0].image_url} alt={product.name} />
                          ) : (
                            <div className={styles.noImage}>📦</div>
                          )}
                        </div>
                        <div className={styles.suggestionInfo}>
                          <div className={styles.suggestionName}>{product.name}</div>
                          <div className={styles.suggestionPrice}>
                            {product.promotional_price ? (
                              <>
                                <span className={styles.oldPrice}>{parseFloat(product.price || 0).toLocaleString('fr-FR')} FCFA</span>
                                <span className={styles.newPrice}>{parseFloat(product.promotional_price).toLocaleString('fr-FR')} FCFA</span>
                              </>
                            ) : (
                              <span>{parseFloat(product.price || 0).toLocaleString('fr-FR')} FCFA</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {!isSearching && searchQuery.trim().length >= 2 && (
                      <div
                        className={styles.suggestionFooter}
                        onClick={() => {
                          navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                          setShowSuggestions(false);
                        }}
                      >
                        Voir tous les résultats pour "{searchQuery}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            </form>
          </div>

              <div className={styles.headerActions}>
                {isAuthenticated ? (
                  <>
                    <Link to="/profile" title="Mon profil">
                      <FontAwesomeIcon icon={faUser} />
                      <span className={styles.userName}>
                        {user?.profile?.first_name || user?.name}
                      </span>
                    </Link>
                    <Link to="/orders" title="Mes commandes">
                      📦 Commandes
                    </Link>
                    <Link to="/wishlist" title="Mes favoris">
                      ❤️ Favoris
                    </Link>
                    {user?.user_type === 'admin' && (
                      <Link to="/admin" title="Tableau de bord admin">
                        👑 Admin
                      </Link>
                    )}
                    <Link to="/cart" className={styles.cartLink} title="Mon panier">
                      <FontAwesomeIcon icon={faShoppingCart} />
                      {totalItems > 0 && (
                        <span className={styles.cartBadge}>{totalItems}</span>
                      )}
                    </Link>
                    <button onClick={handleLogout} title="Déconnexion">
                      <FontAwesomeIcon icon={faSignOutAlt} />
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" title="Connexion">
                      <FontAwesomeIcon icon={faUser} />
                    </Link>
                    <Link to="/cart" className={styles.cartLink} title="Mon panier">
                      <FontAwesomeIcon icon={faShoppingCart} />
                      {totalItems > 0 && (
                        <span className={styles.cartBadge}>{totalItems}</span>
                      )}
                    </Link>
                    <Link to="/login" title="Favoris">
                      <FontAwesomeIcon icon={faHeart} />
                    </Link>
                  </>
                )}
          </div>

          {/* Bouton menu hamburger pour mobile */}
          <button
            className={styles.menuToggle}
            onClick={toggleMenu}
            aria-label="Ouvrir le menu"
          >
            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
          </button>
        </div>

        {/* Navigation desktop */}
        <nav className={`${styles.nav} ${styles.desktopNav}`}>
          <ul className={styles.navLinks}>
            <li>
              <a
                href="#"
                onClick={handleHomeClick}
                className={activeSection === 'home' ? styles.active : ''}
              >
                Accueil
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={handleProductsClick}
                className={activeSection === 'products' ? styles.active : ''}
              >
                Produits
              </a>
            </li>
            {!isLoadingCategories && categories.slice(0, 6).map((category) => (
              <li key={category.id}>
                <a
                  href="#"
                  onClick={(e) => handleCategoryClick(category.id, e)}
                  className={categoryFilter === category.id.toString() ? styles.active : ''}
                >
                  {category.name}
                </a>
              </li>
            ))}
            {!isLoadingCategories && categories.length > 6 && (
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                    setTimeout(() => {
                      const productsSection = document.getElementById('products-section');
                      if (productsSection) {
                        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                >
                  Plus...
                </a>
              </li>
            )}
            {isLoadingCategories && (
              <>
                <li><a href="#">Chargement...</a></li>
              </>
            )}
          </ul>
        </nav>
      </div>

      {/* Overlay et sidebar pour mobile */}
      <div
        className={`${styles.mobileOverlay} ${isMenuOpen ? styles.active : ''}`}
        onClick={closeMenu}
      ></div>

      <div className={`${styles.mobileSidebar} ${isMenuOpen ? styles.active : ''}`}>
        <div className={styles.sidebarHeader}>
          <h3>Menu</h3>
          <button
            className={styles.closeButton}
            onClick={closeMenu}
            aria-label="Fermer le menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <nav className={styles.mobileNav}>
          <ul className={styles.mobileNavLinks}>
            <li>
              <a
                href="#"
                onClick={(e) => { handleHomeClick(e); }}
                className={activeSection === 'home' ? styles.active : ''}
              >
                Accueil
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { handleProductsClick(e); }}
                className={activeSection === 'products' ? styles.active : ''}
              >
                Produits
              </a>
            </li>
            {!isLoadingCategories && categories.slice(0, 6).map((category) => (
              <li key={category.id}>
                <a
                  href="#"
                  onClick={(e) => handleCategoryClick(category.id, e)}
                  className={categoryFilter === category.id.toString() ? styles.active : ''}
                >
                  {category.name}
                </a>
              </li>
            ))}
            {!isLoadingCategories && categories.length > 6 && (
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMenu();
                    navigate('/');
                    setTimeout(() => {
                      const productsSection = document.getElementById('products-section');
                      if (productsSection) {
                        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                >
                  Plus...
                </a>
              </li>
            )}
            {isLoadingCategories && (
              <li><a href="#" onClick={closeMenu}>Chargement...</a></li>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;